import React from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Box, Avatar, Tooltip, Typography, Backdrop, Divider, Button, TableSortLabel, Chip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Close, DragIndicator, UnfoldMore } from '@mui/icons-material';
import { type OfferStandalonePackageDTO } from '../../services/offerStandaloneManageService';

interface Props {
  rows: OfferStandalonePackageDTO[];
  onEdit?: (row: OfferStandalonePackageDTO) => void;
  onDelete?: (row: OfferStandalonePackageDTO) => void;
  onOpenDocumentation?: (row: OfferStandalonePackageDTO) => void;
  onOpenPackageDocumentation?: (row: OfferStandalonePackageDTO) => void;
  onCreateInquiry?: (row: OfferStandalonePackageDTO) => void;
  onAddClick?: () => void;
}


const OfferPackagesTable: React.FC<Props> = ({ rows, onEdit, onDelete, onOpenDocumentation, onOpenPackageDocumentation, onCreateInquiry, onAddClick }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = React.useState<OfferStandalonePackageDTO | null>(null);

  // Tryb zmiany szerokości kolumn
  const headerCellSx: any = React.useMemo(() => ({
    backgroundColor: (t: any) => (t.palette.mode === 'light' ? t.palette.grey[100] : 'rgba(255,255,255,0.06)'),
    position: 'relative'
  }), []);
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number; rightKey?: string; startWRight?: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    id: 180,
    kp: 56,
    name: 250,
    type: 100,
    desc: 200,
    params: 200,
    est: 150,
    inquiries: 60,
    offers: 60,
    actions: 80,
  }), []);
  const STORAGE_KEY = React.useMemo(() => 'offerPackagesColWidths', []);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getWidthNum = React.useCallback((key: string) => columnWidths[key] ?? defaultColumnWidths[key] ?? 40, [columnWidths, defaultColumnWidths]);
  const getColW = React.useCallback((key: string) => `${getWidthNum(key)}px`, [getWidthNum]);

  // Konfiguracja widoczności i kolejności kolumn
  const ALL_COLUMNS = React.useMemo<string[]>(() => ['id','kp','name','type','desc','params','est','inquiries','offers','actions'], []);
  const STORAGE_PREFIX = 'offerPackagesTableCol';
  const VISIBLE_KEY = `${STORAGE_PREFIX}:visible`;
  const ORDER_KEY = `${STORAGE_PREFIX}:order`;
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const ord = JSON.parse(raw);
        if (Array.isArray(ord) && ord.length) return ord.filter((k:string)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!ord.includes(k)));
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {}
    return {};
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);

  // Panel „Dostosuj kolumny”
  const [colsOpen, setColsOpen] = React.useState<boolean>(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<string[] | null>(null);
  const [draftWidths, setDraftWidths] = React.useState<Record<string, number>>({});
  const [colsPosition, setColsPosition] = React.useState<{ top:number; left:number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = React.useState<{ startX:number; startY:number; top:number; left:number } | null>(null);
  const COLUMNS_DIALOG = { width: 305, height: 420 } as const;

  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, colsDragging.top + dy));
      const nextLeft = Math.max(0, Math.min((window?.innerWidth || 0), colsDragging.left + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [colsDragging]);

  // Inicjalizacja szerokości z localStorage (tylko raz przy starcie)
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setColumnWidths((prev) => {
            // Połącz zapisane wartości z domyślnymi, aby upewnić się że wszystkie kolumny mają szerokość
            return { ...defaultColumnWidths, ...parsed };
          });
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Tylko raz przy montowaniu

  // Zapis ustawień do localStorage
  React.useEffect(() => { 
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder)); } catch {} 
  }, [ORDER_KEY, columnOrder]);
  
  React.useEffect(() => { 
    try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible)); } catch {} 
  }, [VISIBLE_KEY, columnsVisible]);
  
  React.useEffect(() => { 
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {} 
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, (dragCol.startWRight as number) - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => { window.removeEventListener('mousemove', onMove); };
  }, [dragCol]);

  // Obsługa ESC do zamykania okienka "Dostosuj kolumny"
  React.useEffect(() => {
    if (!colsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        setColsOpen(false);
        setColResizeMode(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); };
  }, [colsOpen]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center', sortable: boolean = false, tooltipTitle?: string) => {
    const sortKey = keyId as SortKey;
    const isSortable = sortable && (sortKey === 'id' || sortKey === 'kp' || sortKey === 'name' || sortKey === 'type' || sortKey === 'desc' || sortKey === 'params' || sortKey === 'est' || sortKey === 'inquiries' || sortKey === 'offers');
    const content = keyId === 'id' ? <Box sx={{ pl: '16px' }}>{node}</Box> : node;
    const wrappedContent = tooltipTitle ? (
      <Tooltip title={tooltipTitle} arrow>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', width: '100%' }}>
          {content}
        </span>
      </Tooltip>
    ) : content;
    
    return (
    <TableCell align={align} sx={{ padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none', backgroundColor: '#FAFAFA', borderRight: keyId === 'actions' ? 'none' : '1px solid #d4d4d9' }}>
      {isSortable ? (
        <TableSortLabel {...getSortLabelProps(sortKey)}>
          {wrappedContent}
        </TableSortLabel>
      ) : wrappedContent}
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            const visOrder = (draftOrder ?? columnOrder).filter((k)=> colV(k));
            const idx = visOrder.indexOf(keyId);
            const rightKey = idx >= 0 && idx < visOrder.length - 1 ? visOrder[idx+1] : undefined;
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: getWidthNum(keyId),
              rightKey,
              startWRight: rightKey ? getWidthNum(rightKey) : undefined
            });
          }}
          sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }}
        />
      ) : null}
    </TableCell>
    );
  };

  // Sortowanie
  type SortKey = 'id' | 'kp' | 'name' | 'type' | 'desc' | 'params' | 'est' | 'inquiries' | 'offers';
  const [orderBy, setOrderBy] = React.useState<SortKey>('id');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  
  const handleRequestSort = (property: SortKey) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      setOrder('asc');
    }
  };

  // Funkcja do wyodrębnienia liczby porządkowej z package_id (format: P.[liczba].OF...)
  const extractSequenceNumber = (packageId: string): number => {
    if (!packageId) return 0;
    const match = packageId.match(/^P\.(\d+)\./);
    return match ? parseInt(match[1], 10) : 0;
  };

  const sortedRows = React.useMemo(() => {
    const arr = [...rows];
    const dir = order === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (orderBy) {
        case 'id': {
          // Sortowanie numeryczne po liczbie porządkowej
          const aSeq = extractSequenceNumber(a.package_id);
          const bSeq = extractSequenceNumber(b.package_id);
          if (aSeq !== bSeq) return (aSeq - bSeq) * dir;
          // Jeśli liczby są równe, sortuj alfabetycznie po całym ID
          return String(a.package_id || '').localeCompare(String(b.package_id || '')) * dir;
        }
        case 'kp': {
          const av = String((a as any).created_by_name || '');
          const bv = String((b as any).created_by_name || '');
          return av.localeCompare(bv) * dir;
        }
        case 'name': {
          const av = String(a.name || '');
          const bv = String(b.name || '');
          return av.localeCompare(bv) * dir;
        }
        case 'type': {
          const av = String(a.package_type || '');
          const bv = String(b.package_type || '');
          return av.localeCompare(bv) * dir;
        }
        case 'desc': {
          const av = String((a as any).description || '');
          const bv = String((b as any).description || '');
          return av.localeCompare(bv) * dir;
        }
        case 'params': {
          const av = String((a as any).parameters || '');
          const bv = String((b as any).parameters || '');
          return av.localeCompare(bv) * dir;
        }
        case 'est': {
          const av = Number((a as any).estimated_value || 0);
          const bv = Number((b as any).estimated_value || 0);
          return (av - bv) * dir;
        }
        case 'inquiries': {
          const av = Number((a as any).inquiries_count || 0);
          const bv = Number((b as any).inquiries_count || 0);
          return (av - bv) * dir;
        }
        case 'offers': {
          const av = Number((a as any).offers_count || 0);
          const bv = Number((b as any).offers_count || 0);
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    });
    return arr;
  }, [rows, orderBy, order]);

  const totalEstimated = React.useMemo(() => {
    try {
      return rows.reduce((acc, r) => {
        const val = (r as any).estimated_value;
        return acc + (typeof val === 'number' ? val : Number(val || 0));
      }, 0);
    } catch { return 0; }
  }, [rows]);

  const totalInquiries = React.useMemo(() => {
    try {
      return rows.reduce((acc, r) => {
        const val = (r as any).inquiries_count;
        return acc + (typeof val === 'number' ? val : Number(val || 0));
      }, 0);
    } catch { return 0; }
  }, [rows]);

  const totalOffers = React.useMemo(() => {
    try {
      return rows.reduce((acc, r) => {
        const val = (r as any).offers_count;
        return acc + (typeof val === 'number' ? val : Number(val || 0));
      }, 0);
    } catch { return 0; }
  }, [rows]);

  const openMenu = (event: React.MouseEvent<HTMLElement>, row: OfferStandalonePackageDTO) => {
    event.stopPropagation();
    setMenuRow(row);
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const StrongSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
  
  const getSortLabelProps = (keyId: SortKey) => {
    const isActive = orderBy === keyId;
    return {
      IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as any,
      active: isActive,
      direction: isActive ? order : 'asc' as 'asc' | 'desc',
      hideSortIcon: false,
      onClick: () => handleRequestSort(keyId),
      sx: {
        '& .MuiTableSortLabel-icon': {
          color: isActive ? '#2D2D32' : '#d4d4d9'
        }
      }
    };
  };
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Pasek akcji – nad tabelą, wyrównany do prawej */}
      <Box sx={{ position: 'absolute', top: -36, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
        {/* Grupa akcji */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onAddClick && (
            <Tooltip title="Dodaj pakiet">
              <span>
                <IconButton onClick={onAddClick} data-hotkey-add="true">
                  <Box component="img" src="/assets/icons/add.svg" alt="Dodaj" sx={{ width: 22, height: 22 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {/* Odstęp 16px między grupami ikon */}
        <Box sx={{ width: 16 }} />
        {/* Dostosuj kolumny – ostatnia ikona, na prawej krawędzi */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              alignSelf: 'flex-end',
              bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={() => { 
              setDraftColumnsVisible({ ...columnsVisible });
              setDraftOrder([...columnOrder]);
              setDraftWidths({ ...columnWidths });
              setColResizeMode(true); 
              setColsOpen(true); 
              setColsPosition({ top: 0, left: 0 }); 
            }}
          >
            <Box
              component="img"
              alt="kolumny"
              src={'/assets/icons/frame-344.svg?v=2'}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      </Box>

    <TableContainer 
      component={Paper} 
      sx={{ 
        width: '100%',
        mt: 0,
        height: '455px',
        overflow: 'auto',
        position: 'relative',
        border: '1px solid #d0d0d6',
        borderBottom: '1px solid #d0d0d6',
        borderRadius: 0,
        borderTopRightRadius: 4,
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        overflowX: 'hidden',
        overflowY: 'auto',
        boxShadow: 'none',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { width: 0, height: 0 }
      }}
    >
      <Table 
        size="small"
        stickyHeader
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-footer': { position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 },
          '& .MuiTableRow-footer > *:last-child': { borderRight: 'none', borderBottomRightRadius: 4 }
        }}
      >
        {(() => {
          const ord = (draftOrder ?? columnOrder);
          return (
            <colgroup>
              {ord.filter((k)=>k!=='actions').map((key) => (
                colV(key) ? <col key={`col-${key}`} style={{ width: getColW(key) }} /> : null
              ))}
              {colV('actions') ? <col key="col-actions" style={{ width: getColW('actions') }} /> : null}
            </colgroup>
          );
        })()}
        <TableHead>
          <TableRow>
            {(draftOrder ?? columnOrder).filter((k)=>k !== 'actions').map((key) => colV(key) ? renderResizableTh(key, (
              key === 'id' ? 'ID pakietu' :
              key === 'kp' ? 'U' :
              key === 'name' ? 'Nazwa pakietu' :
              key === 'type' ? 'Rodzaj' :
              key === 'desc' ? 'Opis' :
              key === 'params' ? 'Parametry' :
              key === 'est' ? 'Estymacja' :
              key === 'inquiries' ? 'Z' :
              key === 'offers' ? 'O' :
              ''
            ), key === 'est' || key === 'inquiries' || key === 'offers' ? 'center' : key === 'kp' ? 'center' : undefined, true, key === 'inquiries' ? 'Zapytania' : key === 'offers' ? 'Oferty' : key === 'kp' ? 'Utworzył/a' : undefined) : null)}
            {colV('actions') ? renderResizableTh('actions', '', 'center') : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={(draftOrder ?? columnOrder).filter((k)=>colV(k)).length} align="center">
                <Typography variant="body2" sx={{ py: 2, fontFamily: 'Roboto, sans-serif', color: '#2D2D32' }}>
                  Brak pakietów
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((p) => {
              const ord = (draftOrder ?? columnOrder);
              return (
                <TableRow 
                  key={p.package_id} 
                  hover
                  sx={{ 
                    '&:hover': { backgroundColor: '#F5F5FF !important' },
                    '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' }
                  }}
                >
                  {colV('id') ? (
                    <TableCell sx={{ width: getColW('id') }}>
                      <Box sx={{ pl: '16px' }}>{p.package_id}</Box>
                    </TableCell>
                  ) : null}
                  {colV('kp') ? (
                    <TableCell align="center" sx={{ width: getColW('kp') }}>
                      {(() => {
                        const avatarUrl = (p as any).created_by_avatar_url;
                        const userName = (p as any).created_by_name;
                        const hasUser = (avatarUrl && avatarUrl.trim()) || (userName && userName.trim());
                        
                        if (hasUser) {
                          return (
                            <Tooltip title={userName || ''} arrow>
                              <Avatar src={avatarUrl && avatarUrl.trim() ? avatarUrl : undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                                {(userName && userName.trim()) ? userName.substring(0, 1).toUpperCase() : '?'}
                              </Avatar>
                            </Tooltip>
                          );
                        } else {
                          return (
                            <Tooltip title="AI" arrow>
                              <Avatar sx={{ width: 25, height: 25, fontSize: 12, bgcolor: 'transparent', p: 0 }}>
                                <Box component="img" src="/assets/ai_badge_unitresco.svg" alt="AI" sx={{ width: 25, height: 25 }} />
                              </Avatar>
                            </Tooltip>
                          );
                        }
                      })()}
                    </TableCell>
                  ) : null}
                  {colV('name') ? (<TableCell sx={{ width: getColW('name') }}>{p.name}</TableCell>) : null}
                  {colV('type') ? (<TableCell sx={{ width: getColW('type') }}>{p.package_type || '-'}</TableCell>) : null}
                  {colV('desc') ? (
                    <TableCell sx={{ width: getColW('desc') }}>
                      {(() => {
                        const desc = (p as any).description;
                        const hasDesc = typeof desc === 'string' ? desc.trim().length > 0 : Boolean(desc);
                        if (!hasDesc) return '-';
                        return (
                          <Box sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal'
                          }}>
                            {String(desc)}
                          </Box>
                        );
                      })()}
                    </TableCell>
                  ) : null}
                  {colV('params') ? (
                    <TableCell sx={{ width: getColW('params') }}>
                      {(() => {
                        const params = (p as any).parameters;
                        const hasParams = typeof params === 'string' ? params.trim().length > 0 : Boolean(params);
                        if (!hasParams) return '-';
                        return (
                          <Box sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal'
                          }}>
                            {String(params)}
                          </Box>
                        );
                      })()}
                    </TableCell>
                  ) : null}
                  {colV('est') ? (
                    <TableCell align="right" sx={{ width: getColW('est') }}>
                      {(p as any).estimated_value !== undefined && (p as any).estimated_value !== null 
                        ? (Number((p as any).estimated_value) / 1000).toLocaleString('pl-PL')
                        : '-'}
                    </TableCell>
                  ) : null}
                  {colV('inquiries') ? (
                    <TableCell align="center" sx={{ width: getColW('inquiries') }}>
                      {(() => {
                        const count = Number((p as any).inquiries_count || 0);
                        const chipColor = count === 0 ? '#FFE6E6' : count <= 2 ? '#FEEED6' : '#EAF7EA';
                        const textColor = '#020242';
                        return (
                          <Tooltip title="Zapytania" arrow>
                            <Chip
                              label={count}
                              size="small"
                              sx={{
                                bgcolor: chipColor,
                                color: textColor,
                                borderRadius: '4px',
                                minHeight: 28,
                                height: 28,
                                '& .MuiChip-label': {
                                  px: 1,
                                  py: 0,
                                  fontFamily: 'Roboto, sans-serif',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                  color: textColor
                                }
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  ) : null}
                  {colV('offers') ? (
                    <TableCell align="center" sx={{ width: getColW('offers') }}>
                      {(() => {
                        const count = Number((p as any).offers_count || 0);
                        const chipColor = count === 0 ? '#FFE6E6' : count <= 2 ? '#FEEED6' : '#EAF7EA';
                        const textColor = '#020242';
                        return (
                          <Tooltip title="Oferty" arrow>
                            <Chip
                              label={count}
                              size="small"
                              sx={{
                                bgcolor: chipColor,
                                color: textColor,
                                borderRadius: '4px',
                                minHeight: 28,
                                height: 28,
                                '& .MuiChip-label': {
                                  px: 1,
                                  py: 0,
                                  fontFamily: 'Roboto, sans-serif',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                  color: textColor
                                }
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  ) : null}
                  {colV('actions') ? (
                    <TableCell align="center" sx={{ width: getColW('actions') }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton size="small" onClick={(e) => openMenu(e, p)} aria-label="więcej">
                          <MoreVertIcon fontSize="small" sx={{ color: '#2D2D32' }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            {(draftOrder ?? columnOrder).filter((k)=>k !== 'actions' && k !== 'est' && k !== 'inquiries' && k !== 'offers').map((key) => colV(key) ? (
              <TableCell key={key} sx={{ width: getColW(key) }}></TableCell>
            ) : null)}
            {colV('est') ? (
              <TableCell sx={{ width: getColW('est'), fontWeight: 700 }} align="right">
                Suma {new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalEstimated / 1000)}
              </TableCell>
            ) : null}
            {colV('inquiries') ? (
              <TableCell sx={{ width: getColW('inquiries'), fontWeight: 700 }} align="center">
                {totalInquiries}
              </TableCell>
            ) : null}
            {colV('offers') ? (
              <TableCell sx={{ width: getColW('offers'), fontWeight: 700 }} align="center">
                {totalOffers}
              </TableCell>
            ) : null}
            {colV('actions') ? (
              <TableCell sx={{ width: getColW('actions') }}></TableCell>
            ) : null}
          </TableRow>
        </TableFooter>
      </Table>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {menuRow && onOpenPackageDocumentation && (
          <MenuItem onClick={() => { if (menuRow) onOpenPackageDocumentation(menuRow); closeMenu(); }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Box component="img" src="/assets/icons/package.svg" alt="Dokumentacja pakietu" sx={{ width: 18, height: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Dokumentacja pakietu" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {menuRow && onOpenDocumentation && (
          <MenuItem onClick={() => { if (menuRow) onOpenDocumentation(menuRow); closeMenu(); }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Box component="img" src="/assets/icons/folder.svg" alt="Dokumentacja całkowita" sx={{ width: 18, height: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Dokumentacja całkowita" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {menuRow && onCreateInquiry && (
          <MenuItem onClick={() => { if (menuRow) onCreateInquiry(menuRow); closeMenu(); }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Box component="img" src="/assets/icons/inquiry.svg" alt="Utwórz zapytanie" sx={{ width: 18, height: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Utwórz zapytanie" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {menuRow && onEdit && (
          <MenuItem onClick={() => { if (menuRow && onEdit) onEdit(menuRow); closeMenu(); }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Box component="img" src="/assets/icons/edit2.svg" alt="Edytuj" sx={{ width: 18, height: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Edytuj" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {menuRow && onDelete && (
          <MenuItem onClick={() => { if (menuRow && onDelete) onDelete(menuRow); closeMenu(); }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Box component="img" src="/assets/icons/delete2.svg" alt="Usuń" sx={{ width: 18, height: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Usuń" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
      </Menu>
    </TableContainer>

      {/* Okno „Dostosuj kolumny” – spójne z tabelą projektów */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }} onClick={()=> { 
        setColsOpen(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: `${COLUMNS_DIALOG.height}px`,
            width: `${COLUMNS_DIALOG.width}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto'
          }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
                <IconButton size="small" onClick={()=>{ 
                  setColsOpen(false); 
                  setColResizeMode(false); 
                  setDraftColumnsVisible(null);
                  setDraftOrder(null);
                  setDraftWidths({});
                }}>
                  <Close fontSize="small" />
                </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e)=>{ e.preventDefault(); }}
                  onDrop={(e)=>{ e.preventDefault(); const src = e.dataTransfer.getData('text/plain'); if (!src || src===key) return; setDraftOrder(prev=>{ const cur = [...(prev||ord)]; const from = cur.indexOf(src); const to = cur.indexOf(key); if (from<0||to<0) return cur; cur.splice(to,0,cur.splice(from,1)[0]); return cur; }); }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{(key === 'id' ? 'ID pakietu' : key === 'kp' ? 'U' : key === 'name' ? 'Nazwa pakietu' : key === 'type' ? 'Rodzaj' : key === 'desc' ? 'Opis' : key === 'params' ? 'Parametry' : key === 'est' ? 'Estymacja' : key === 'inquiries' ? 'Z' : key === 'offers' ? 'O' : key.toUpperCase())}</Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ 
              setColsOpen(false); 
              setColResizeMode(false); 
              setDraftColumnsVisible(null);
              setDraftOrder(null);
              setDraftWidths({});
            }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ 
              if (draftColumnsVisible) { 
                setColumnsVisible(draftColumnsVisible); 
                try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(draftColumnsVisible)); } catch {} 
              } 
              if (draftOrder) { 
                const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); 
                setColumnOrder(normalized); 
                try { localStorage.setItem(ORDER_KEY, JSON.stringify(normalized)); } catch {} 
              }
              // Zapisz szerokości kolumn - użyj aktualnych szerokości (mogły być zmienione przez przeciąganie)
              // Jeśli są zmiany w draftWidths, użyj ich, w przeciwnym razie użyj aktualnych columnWidths
              const widthsToSave = { ...columnWidths };
              const finalOrder = draftOrder ?? columnOrder;
              finalOrder.forEach((key) => {
                const draftW = draftWidths[key];
                if (draftW && typeof draftW === 'number' && draftW >= 24) {
                  widthsToSave[key] = draftW;
                }
              });
              // Zapisz wszystkie aktualne szerokości
              setColumnWidths(widthsToSave);
              try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(widthsToSave));
              } catch {}
              setColsOpen(false); 
              setColResizeMode(false);
              setDraftColumnsVisible(null);
              setDraftOrder(null);
              setDraftWidths({});
            }}>
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default OfferPackagesTable;


